import { useCallback, useRef, useEffect } from 'react';
import debounce from '../utils/debounce';
import { addCommas } from '@persian-tools/persian-tools';
import { useGetDaysBetweenDates } from '@/hooks/useGetDaysBetweenTwoDates';
const useDatePicker = ({
  checkInDate,
  checkOutDate,
  hoveredDate,
  onDayClick,
  onDayHover,
  todayDate,
  nextRangeDays,
  pickerDate,
  onlyDays = false,
  withTooltip
}) => {
  const {
    getDaysBetweenDates
  } = useGetDaysBetweenDates();
  const dayRefs = useRef({});

  // Debounced handlers
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedHover = useCallback(debounce(item => {
    onDayHover(!isDayDisabled(item) ? item : null);
  }, 100), [onDayHover]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedLeave = useCallback(debounce(() => {
    onDayHover(null);
  }, 100), [onDayHover]);

  // Store debounced handlers in refs
  const hoverRef = useRef(debouncedHover);
  const leaveRef = useRef(debouncedLeave);

  // Update refs when handlers change
  useEffect(() => {
    hoverRef.current = debouncedHover;
    leaveRef.current = debouncedLeave;
  }, [debouncedHover, debouncedLeave]);
  const isDayDisabled = useCallback(day => {
    return day.ts > nextRangeDays?.ts || todayDate?.ts > day.ts || day.is_day_disabled || checkInDate && checkInDate.ts >= day.ts && day.is_day_disabled;
  }, [nextRangeDays?.ts, todayDate.ts, checkInDate]);
  const isSameDay = useCallback(item => {
    return checkOutDate?.day === item.day && checkOutDate?.month === item.month;
  }, [checkOutDate?.day, checkOutDate?.month]);
  const getType = useCallback((item, index) => {
    if (!onlyDays) {
      return (item.day + pickerDate.firstDayOfMonthWeekDay - 1) % 7 < 5 ? 'Right' : 'Left';
    } else {
      return index % 7 > 4 ? 'Left' : 'Right';
    }
  }, [onlyDays, pickerDate.firstDayOfMonthWeekDay]);
  const formatPrice = useCallback(amount => {
    const formattedAmount = amount >= 1000 ? Math.round(amount / 1000) : amount;
    return formattedAmount >= 1000 ? addCommas(formattedAmount) : formattedAmount;
  }, []);
  const handleDayClick = useCallback(item => {
    if (!isDayDisabled(item)) {
      onDayClick(item);
    }
  }, [isDayDisabled, onDayClick]);
  const getStateAndClassNames = useCallback(item => {
    const state = {
      isSelectedInRangeDate: false,
      isInRangeDate: false,
      isCheckinSelectedInRangeDate: false,
      isCheckinSelectedDate: false,
      isCheckoutSelectedInRangeDate: false,
      isCheckoutSelectedDate: false,
      isDayDisabled: false,
      isHoliday: false,
      isReserved: false,
      isFutureDay: false
    };

    // ClassNames array
    const classNames = ['day-tag'];
    if (checkInDate && (hoveredDate || checkOutDate) && item.ts <= (checkOutDate?.ts || hoveredDate.ts) && item.ts >= checkInDate.ts) {
      state.isSelectedInRangeDate = !!checkOutDate;
      state.isInRangeDate = !checkOutDate;
      classNames.push(checkOutDate ? 'selected-in-range-date' : 'in-range-date');
    }
    if (checkInDate && item.ts === checkInDate.ts) {
      state.isCheckinSelectedInRangeDate = true;
      state.isCheckinSelectedDate = true;
      classNames.push('checkin-selected-in-range-date', 'checkin-selected-date');
    }
    if (checkInDate && (hoveredDate || checkOutDate) && item.ts == (checkOutDate?.ts || hoveredDate.ts)) {
      state.isCheckoutSelectedInRangeDate = true;
      state.isCheckoutSelectedDate = true;
      classNames.push(checkOutDate ? 'checkout-selected-date' : 'checkout-selected-in-range-date');
    }
    if ((item.is_day_disabled || todayDate.ts > item.ts) && item.ts <= nextRangeDays.ts && !(checkInDate && hoveredDate && item.ts <= hoveredDate.ts && item.ts >= checkInDate.ts && checkOutDate) || item.is_day_disabled && checkInDate?.ts >= item.ts) {
      state.isDayDisabled = true;
      classNames.push('day-is-disabled');
    }
    if (item.isPeak && todayDate.ts <= item.ts) {
      state.isHoliday = true;
      classNames.push('day-is-holiday');
    }
    if (item.is_day_disabled && todayDate.ts <= item.ts && item.ts <= nextRangeDays.ts) {
      state.isReserved = true;
      classNames.push('day-is-reserved');
    }
    if (item.ts > nextRangeDays.ts) {
      state.isFutureDay = true;
      classNames.push('future-days');
    }
    return {
      state,
      classNames
    };
  }, [checkInDate, checkOutDate, hoveredDate, nextRangeDays, todayDate.ts]);
  const getClassNames = useCallback(item => {
    let classNames = 'day-tag';

    // Determine class names based on the item and current state
    if (checkInDate && (hoveredDate || checkOutDate) && item.ts <= (checkOutDate?.ts || hoveredDate.ts) && item.ts >= checkInDate.ts) {
      classNames += checkOutDate ? ' selected-in-range-date' : ' in-range-date';
    }
    if (checkInDate && item.day === checkInDate.day && item.month === checkInDate.month) {
      classNames += ' checkin-selected-in-range-date checkin-selected-date';
    }
    if (checkOutDate && item.day === checkOutDate.day && item.month === checkOutDate.month) {
      classNames += ' checkout-selected-in-range-date checkout-selected-date';
    }
    if ((item.is_day_disabled || todayDate.ts > item.ts) && item.ts <= nextRangeDays.ts && !(checkInDate && hoveredDate && item.ts <= hoveredDate.ts && item.ts >= checkInDate.ts && checkOutDate) || item.is_day_disabled && checkInDate?.ts >= item.ts) {
      classNames += ' day-is-disabled';
    }
    if (item.isPeak && todayDate.ts <= item.ts) {
      classNames += ' day-is-holiday';
    }
    if (!item.min_book_day && item.is_day_disabled && todayDate.ts <= item.ts && item.ts <= nextRangeDays.ts) {
      classNames += ' day-is-reserved';
    }
    if (item.ts > nextRangeDays.ts) {
      classNames += ' future-days';
    }
    return classNames;
  }, [checkInDate, checkOutDate, hoveredDate, nextRangeDays, todayDate.ts]);
  const getDatePickerTooltipData = useCallback((item, index) => {
    if (withTooltip) {
      if ((item.is_day_disabled || item.min_book_day > 1) && (item.is_day_disabled || (checkInDate || {}).ts === item.ts) && (checkOutDate || {}).ts !== item.ts && todayDate.ts <= item.ts) {
        return {
          type: getType(item, index),
          content: item.min_book_day === 1 ? 'قبلا رزرو شده است' : `حداقل مدت رزرو ${item.min_book_day} شب`
        };
      } else if (isSameDay(item)) {
        return {
          type: getType(item, index),
          content: ` ${getDaysBetweenDates(checkInDate.ts, checkOutDate.ts)} شب اقامت `
        };
      }
    }
    return null;
  }, [checkInDate, checkOutDate, todayDate, getDaysBetweenDates, getType, isSameDay, withTooltip]);
  return {
    dayRefs,
    hoverRef,
    leaveRef,
    isDayDisabled,
    getClassNames,
    handleDayClick,
    getDatePickerTooltipData,
    formatPrice,
    getStateAndClassNames
  };
};
export default useDatePicker;